import { useEffect, useRef, useState } from 'react';
import { speak } from '../../lib/conversationsApi';
import './messageMenu.css';

interface MessageMenuProps {
  text: string;
  // Real per-message cost from the chat response -- only shown when the
  // backend actually returned one, never fabricated.
  credits?: number;
}

export function MessageMenu({ text, credits }: MessageMenuProps) {
  const [open, setOpen] = useState(false);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState('');
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const readAloud = async () => {
    setError('');
    setReading(true);
    try {
      const result = await speak({ text });
      if (result.audio) {
        const audio = new Audio(result.audio);
        audioRef.current = audio;
        audio.onended = () => setReading(false);
        audio.onerror = () => { setReading(false); setError('Could not play the audio.'); };
        await audio.play();
      } else {
        setReading(false);
        setError(result.error || result.notice || 'Voice output is not available.');
      }
    } catch (_) {
      setReading(false);
      setError('Could not read this message aloud.');
    }
  };

  return (
    <div className="ozira-message-menu" ref={rootRef}>
      <button
        type="button"
        className="ozira-response-action"
        aria-label="More options"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span aria-hidden="true" style={{ fontSize: 18, lineHeight: 1 }}>⋯</span>
      </button>
      {open ? (
        <div className="ozira-message-menu__popover" role="menu">
          <button
            type="button"
            role="menuitem"
            className="ozira-message-menu__item"
            disabled={reading}
            onClick={() => void readAloud()}
          >
            {reading ? 'Reading…' : 'Read Aloud'}
          </button>
          {error ? <p className="ozira-message-menu__error" role="alert">{error}</p> : null}
          {typeof credits === 'number' ? (
            <>
              <div className="ozira-message-menu__divider" />
              <p className="ozira-message-menu__meta">Used {credits} credit{credits === 1 ? '' : 's'}</p>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
